import React, { useMemo, useState } from 'react';
import {
  GraduationCap, Users, Target, Award, Layers, Eye,
  BookOpen, Search, ChevronLeft, ChevronRight,
} from 'lucide-react';
import type {
  CourseCompletionEntry,
  CategoryBreakdownEntry,
  TopCourse,
  ProgressDistribution,
} from '@/services/PagesServices/AnalyticsService';
import { computeProgressSegments, describeArc, filterAndPaginate } from '../utils';

interface CoursesTabProps {
  courseCompletion: CourseCompletionEntry[];
  categoryBreakdown: CategoryBreakdownEntry[];
  topCourses: TopCourse[];
  progressDistribution: ProgressDistribution | null;
}

const COURSES_PER_PAGE = 10;

const CoursesTab: React.FC<CoursesTabProps> = ({
  courseCompletion, categoryBreakdown, topCourses, progressDistribution,
}) => {
  const [courseSearch, setCourseSearch] = useState('');
  const [coursePage, setCoursePage] = useState(1);

  const courseSummary = useMemo(() => {
    const total = courseCompletion.length;
    const totalEnrollments = courseCompletion.reduce((s, c) => s + c.totalEnrollments, 0);
    const avgCompletion = total > 0 ? Math.round(courseCompletion.reduce((s, c) => s + c.completionRate, 0) / total) : 0;
    const highPerformers = courseCompletion.filter(c => c.completionRate >= 70).length;
    return { total, totalEnrollments, avgCompletion, highPerformers };
  }, [courseCompletion]);

  const progressSegments = useMemo(
    () => computeProgressSegments(progressDistribution),
    [progressDistribution]
  );

  const { filtered: filteredCourses, paginated: paginatedCourses, totalPages: totalCoursePages } = useMemo(
    () => filterAndPaginate(courseCompletion, courseSearch, ['title', 'category'], coursePage, COURSES_PER_PAGE),
    [courseCompletion, courseSearch, coursePage]
  );

  const renderPagination = () => {
    if (totalCoursePages <= 1) return null;
    return (
      <div className="analytics-pagination">
        <span className="analytics-pagination-info">
          Showing {(coursePage - 1) * COURSES_PER_PAGE + 1}–{Math.min(coursePage * COURSES_PER_PAGE, filteredCourses.length)} of {filteredCourses.length}
        </span>
        <div className="analytics-pagination-controls">
          <button disabled={coursePage <= 1} onClick={() => setCoursePage(p => p - 1)}><ChevronLeft size={16} /></button>
          {Array.from({ length: Math.min(totalCoursePages, 5) }, (_, i) => {
            let page: number;
            if (totalCoursePages <= 5) page = i + 1;
            else if (coursePage <= 3) page = i + 1;
            else if (coursePage >= totalCoursePages - 2) page = totalCoursePages - 4 + i;
            else page = coursePage - 2 + i;
            return (
              <button key={page} className={coursePage === page ? 'active' : ''} onClick={() => setCoursePage(page)}>{page}</button>
            );
          })}
          <button disabled={coursePage >= totalCoursePages} onClick={() => setCoursePage(p => p + 1)}><ChevronRight size={16} /></button>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Course Summary Cards */}
      <div className="analytics-summary-cards">
        <div className="analytics-summary-card">
          <div className="analytics-summary-card-icon" style={{ background: '#eef2ff', color: '#6366f1' }}><GraduationCap size={20} /></div>
          <div className="analytics-summary-card-info">
            <div className="analytics-summary-card-value">{courseSummary.total}</div>
            <div className="analytics-summary-card-label">Total Courses</div>
          </div>
        </div>
        <div className="analytics-summary-card">
          <div className="analytics-summary-card-icon" style={{ background: '#fff3e8', color: '#ff8c42' }}><Users size={20} /></div>
          <div className="analytics-summary-card-info">
            <div className="analytics-summary-card-value">{courseSummary.totalEnrollments}</div>
            <div className="analytics-summary-card-label">Total Enrollments</div>
          </div>
        </div>
        <div className="analytics-summary-card">
          <div className="analytics-summary-card-icon" style={{ background: '#dcfce7', color: '#16a34a' }}><Target size={20} /></div>
          <div className="analytics-summary-card-info">
            <div className="analytics-summary-card-value">{courseSummary.avgCompletion}%</div>
            <div className="analytics-summary-card-label">Avg Completion</div>
          </div>
        </div>
        <div className="analytics-summary-card">
          <div className="analytics-summary-card-icon" style={{ background: '#fce7f3', color: '#ec4899' }}><Award size={20} /></div>
          <div className="analytics-summary-card-info">
            <div className="analytics-summary-card-value">{courseSummary.highPerformers}</div>
            <div className="analytics-summary-card-label">High Performers (70%+)</div>
          </div>
        </div>
      </div>

      {/* Category Breakdown + Progress Distribution */}
      <div className="analytics-grid-2">
        {categoryBreakdown.length > 0 && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <Layers size={20} className="analytics-section-icon" />
              Courses by Category
            </div>
            <div className="analytics-section-subtitle">Number of courses and enrollments per category</div>
            <div className="analytics-category-breakdown-list">
              {categoryBreakdown.map((cat, i) => (
                <div key={i} className="analytics-category-row">
                  <div className="analytics-category-row-label">
                    <div className="analytics-category-dot" style={{ background: cat.color }} />
                    <span className="analytics-category-name">{cat.category}</span>
                  </div>
                  <div className="analytics-category-row-stats" style={{ gap: 16 }}>
                    <span className="analytics-category-course-count">{cat.courseCount} courses</span>
                    <span className="analytics-category-enrollment-count">{cat.enrollments} enrolled</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {progressSegments.length > 0 && (
          <div className="analytics-section" style={{ marginBottom: 0 }}>
            <div className="analytics-section-title">
              <Eye size={20} className="analytics-section-icon" />
              Learner Progress Overview
            </div>
            <div className="analytics-section-subtitle">Distribution of learner progress across all courses</div>
            <div className="analytics-progress-donut-container">
              <svg viewBox="0 0 140 140" className="analytics-donut-svg" style={{ width: 140, height: 140 }}>
                {(() => {
                  const total = progressSegments.reduce((s, seg) => s + (seg.value || 0), 0);
                  let cumulative = 0;
                  return progressSegments.map((seg, i) => {
                    if (!seg.value || seg.value === 0) return null;
                    const pct = (seg.value / total) * 100;
                    const start = cumulative;
                    cumulative += pct;
                    return (
                      <path key={i} d={describeArc(70, 70, 50, (start / 100) * 360, ((start + pct) / 100) * 360 - 0.5)} fill="none" stroke={seg.color} strokeWidth="20" strokeLinecap="round" />
                    );
                  });
                })()}
                <text x="70" y="66" textAnchor="middle" fill="var(--color-black-text)" fontSize="18" fontWeight="700">
                  {progressSegments.reduce((s, seg) => s + (seg.value || 0), 0)}
                </text>
                <text x="70" y="82" textAnchor="middle" fill="var(--color-secondary-text)" fontSize="10">Learners</text>
              </svg>
              <div className="analytics-cert-legend">
                {progressSegments.map((seg, i) => (
                  <div key={i} className="analytics-cert-legend-item">
                    <div className="analytics-cert-color" style={{ backgroundColor: seg.color }} />
                    <span className="analytics-cert-type">{seg.label}</span>
                    <span className="analytics-cert-pct">{seg.value} ({seg.pct}%)</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Top Courses Visual */}
      {topCourses.length > 0 && (
        <div className="analytics-section">
          <div className="analytics-section-title">
            <GraduationCap size={20} className="analytics-section-icon" />
            Top Courses
          </div>
          <div className="analytics-section-subtitle">Most enrolled courses on the platform</div>
          <div className="analytics-top-courses-grid">
            {topCourses.map((course, i) => {
              const pct = course.total > 0 ? Math.round((course.enrolled / course.total) * 100) : 0;
              return (
                <div key={i} className="analytics-course-card">
                  <div className="analytics-course-card-rank">#{i + 1}</div>
                  <div className="analytics-course-card-name">{course.name}</div>
                  <div className="analytics-course-card-stats">
                    <span>{course.enrolled} / {course.total} enrolled</span>
                    <span className="analytics-course-card-pct">{pct}%</span>
                  </div>
                  <div className="analytics-progress-bar" style={{ height: '6px' }}>
                    <div className="analytics-progress-fill high" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Course Completion Table */}
      {courseCompletion.length > 0 ? (
        <div className="analytics-section">
          <div className="analytics-section-header-row">
            <div>
              <div className="analytics-section-title">
                <BookOpen size={20} className="analytics-section-icon" />
                Course Completion Details
              </div>
              <div className="analytics-section-subtitle">{filteredCourses.length} of {courseCompletion.length} courses</div>
            </div>
            <div className="analytics-table-search">
              <Search size={16} />
              <input type="text" placeholder="Search courses..." value={courseSearch} onChange={(e) => { setCourseSearch(e.target.value); setCoursePage(1); }} />
            </div>
          </div>
          <div className="analytics-table-wrapper">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Course</th><th>Category</th><th>Enrolled</th><th>Completed</th>
                  <th>Completion Rate</th><th>Avg Time</th><th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCourses.map((course, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{course.title}</td>
                    <td><span className="analytics-category-badge">{course.category}</span></td>
                    <td>{course.totalEnrollments}</td>
                    <td>{course.completed}</td>
                    <td>
                      <div className="analytics-rate-cell">
                        <div className="analytics-progress-bar" style={{ width: '60px', height: '6px' }}>
                          <div className={`analytics-progress-fill ${course.completionRate >= 70 ? 'high' : course.completionRate >= 40 ? 'medium' : 'low'}`} style={{ width: `${course.completionRate}%` }} />
                        </div>
                        <span className={`analytics-rate-value ${course.completionRate >= 70 ? 'high' : course.completionRate >= 40 ? 'medium' : 'low'}`}>{course.completionRate}%</span>
                      </div>
                    </td>
                    <td>{course.avgTimeSpent}h</td>
                    <td>{course.courseDuration}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </div>
      ) : (
        <div className="analytics-section">
          <div className="analytics-empty">
            <BookOpen size={40} />
            <p>No course completion data available</p>
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(CoursesTab);
